import { useRequest } from 'ahooks'
import { Button, Tag, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { DepartmentName, MosaicComponent, RelationsDeleter, UserAvatar, UserName } from '@/components'
import { DevlopStatus } from '@/modules/devlop/devlop.schema'
import { routes } from '@/router'
import { PpRelationAuditStatus, ResourceType } from '@/schemas'

interface ResourceOKRProps {
  selfId: string
  type: ResourceType
  relations: any[]
  canRead: boolean
  refresh: () => void
  refreshRelationAudit: () => void
  disabled?: boolean
  auditRelationData?: any[]
  isSummaryPage?: boolean
}

export const getNameLink = ({
  name,
  selfId,
  type,
  departmentId,
  params,
}: {
  name: string
  selfId: string
  type: ResourceType
  departmentId?: number
  params?: Record<'tab', string | number>
}) => {
  return {
    [ResourceType.STAGE]: name,
    [ResourceType.PROJECT]: (
      <Link to={routes.project.page(selfId, params)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
    ),
    [ResourceType.PORTFOLIO]: (
      <Link to={routes.portfolio.page(selfId)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
    ),
    [ResourceType.OBJECTIVE]: name,
    [ResourceType.TEAM]: (
      <Link to={routes.team.home({ departmentId, ...params })} target='__blank' onClick={(e) => e.stopPropagation()}>
        <DepartmentName canRead={true} departmentId={departmentId!} />
      </Link>
    ),
    [ResourceType.CUSTOMER]: (
      <Link to={routes.customer.detail(selfId, params)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
    ),
    [ResourceType.PRODUCTION]: (
      <Link to={routes.production.detail(selfId, params)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
    ),
    [ResourceType.DEVLOP]: (
      <Link to={routes.devlop.detail(selfId, params)} target='__blank' onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
    ),
  }[type]
}

export const getResourceTag = ({ type, ...props }: any) => {
  let color = ''
  switch (type) {
    case ResourceType.PROJECT:
      color = 'blue'
      break
    case ResourceType.PORTFOLIO:
      color = 'green'
      break
    case ResourceType.OBJECTIVE:
      color = 'orange'
      break
    case ResourceType.CUSTOMER:
      color = 'volcano'
      break
    case ResourceType.PRODUCTION:
      color = 'gold'
      break
    case ResourceType.DEVLOP:
      color = 'cyan'
      break
    case ResourceType.TEAM:
      color = 'geekblue'
      break
  }
  return (
    <Tag color={color} {...props}>
      {ResourceType[type]}
    </Tag>
  )
}

// 关联父项目 --  产品项目、客户项目、研发项目
export const SpecialProjectRelations: React.FC<ResourceOKRProps> = ({
  selfId,
  type,
  canRead,
  relations,
  auditRelationData,
  refresh: refreshRelation,
  refreshRelationAudit,
  disabled = false,
  isSummaryPage = false,
}) => {
  const { t } = useTranslation()
  const DevlopTranslateMap = {
    [DevlopStatus.未开始]: t('devlop.status.notStart'),
    [DevlopStatus.进行中]: t('devlop.status.ongoing'),
    [DevlopStatus.暂停]: t('devlop.status.pause'),
    // [DevlopStatus.终止]: t('devlop.status.termination'),
    [DevlopStatus.已结项]: t('devlop.status.finished.title'),
  }

  const refresh = () => {
    refreshRelation()
    refreshRelationAudit()
  }

  const { run } = useRequest<any[]>(
    ({ _selfId, _type, status }) => ({
      url: '/api/relations/updateAudit',
      method: 'post',
      data: {
        selfId,
        type,
        parentSelfId: _selfId,
        parentType: _type,
        status,
      },
    }),
    {
      refreshDeps: [selfId],
      manual: true,
      onSuccess: () => refresh(),
    }
  )

  const onClickAudit = ({
    parentSelfId,
    parentType,
    isRevoke,
  }: {
    parentSelfId: string
    parentType: ResourceType
    isRevoke: boolean
  }) => {
    run({
      _selfId: parentSelfId,
      _type: parentType,
      status: isRevoke ? PpRelationAuditStatus.审批撤回 : PpRelationAuditStatus.移除关系,
    })
  }

  let content = <Typography.Text type='secondary'>{t('project.nodata')}</Typography.Text>

  // 新的走审批的挂靠项目
  const audits =
    (auditRelationData || []).length === 0
      ? null
      : auditRelationData!.map(
          ({
            parentCustomer: customer,
            parentProduction: production,
            parentDevlop: devlop,
            parentSelfId,
            parentType,
            auditStatus,
          }) => {
            const resource =
              parentType === ResourceType.CUSTOMER
                ? customer
                : parentType === ResourceType.PRODUCTION
                  ? production
                  : devlop
            const status =
              parentType === ResourceType.CUSTOMER
                ? t(`customer.flowStatus.${resource.flowStatus}.title`)
                : parentType === ResourceType.PRODUCTION
                  ? t(`production.flowStatus.${resource.flowStatus}.title`)
                  : DevlopTranslateMap[resource.status]
            return (
              <div key={resource.selfId} className='flex items-center group'>
                <UserAvatar className='mr-8px' email={resource.email} />
                <div className='text-xs'>
                  <Typography.Text className='block'>
                    {getNameLink({
                      name: resource.name,
                      type: resource.type,
                      selfId: resource.selfId,
                    })}
                  </Typography.Text>
                  <Typography.Text type='secondary' className='block'>
                    <UserName email={resource.email} />
                    <span> - </span>
                    <span>{t(ResourceType[parentType])}</span>
                    <span> - {status}</span>
                    <span> - {PpRelationAuditStatus[auditStatus]}</span>
                    {!disabled && auditStatus === PpRelationAuditStatus.审批中 && (
                      <Button
                        type='link'
                        size='small'
                        className='text-xs opacity-0 group-hover:opacity-100'
                        onClick={() =>
                          onClickAudit({
                            parentSelfId,
                            parentType,
                            isRevoke: true,
                          })
                        }
                      >
                        {t('撤回')}
                      </Button>
                    )}
                    {!disabled && auditStatus === PpRelationAuditStatus.审批通过 && (
                      <Button
                        type='link'
                        size='small'
                        className='text-xs opacity-0 group-hover:opacity-100'
                        onClick={() =>
                          onClickAudit({
                            parentSelfId,
                            parentType,
                            isRevoke: false,
                          })
                        }
                      >
                        {t('remove')}
                      </Button>
                    )}
                  </Typography.Text>
                </div>
              </div>
            )
          }
        )

  const relationDataWithoutAudit = (relations || []).filter(
    ({ parentSelfId }) =>
      !auditRelationData?.find(
        (a) => a.parentSelfId === parentSelfId && a.auditStatus === PpRelationAuditStatus.审批通过
      )
  )

  // 过去已经挂靠但是没有走审批的项目
  const projects =
    relationDataWithoutAudit.length === 0
      ? null
      : relationDataWithoutAudit.map(
          ({ parentCustomer: customer, parentProduction: production, parentDevlop: devlop, parentType }) => {
            const resource =
              parentType === ResourceType.CUSTOMER
                ? customer
                : parentType === ResourceType.PRODUCTION
                  ? production
                  : devlop

            const status =
              parentType === ResourceType.CUSTOMER
                ? t(`customer.flowStatus.${resource.flowStatus}.title`)
                : parentType === ResourceType.PRODUCTION
                  ? t(`production.flowStatus.${resource.flowStatus}.title`)
                  : DevlopTranslateMap[resource.status]
            return (
              <div key={resource.selfId} className='flex items-center group'>
                <UserAvatar className='mr-8px' email={resource.email} />
                <div className='text-xs'>
                  <Typography.Text className='block'>
                    {getNameLink({
                      name: resource.name,
                      type: resource.type,
                      selfId: resource.selfId,
                    })}
                  </Typography.Text>
                  <Typography.Text type='secondary' className='block'>
                    <UserName email={resource.email} />
                    <span> - </span>
                    <span>{t(ResourceType[parentType])}</span>
                    <span> - </span>
                    <span>{status}</span>
                    {!disabled && (
                      <RelationsDeleter
                        parentSelfId={resource.selfId}
                        parentType={parentType}
                        selfId={selfId}
                        type={type}
                        onSuccess={refresh}
                        isSummaryPage={isSummaryPage}
                      >
                        <Button type='link' size='small' className='text-xs opacity-0 group-hover:opacity-100'>
                          {t('remove')}
                        </Button>
                      </RelationsDeleter>
                    )}
                  </Typography.Text>
                </div>
              </div>
            )
          }
        )

  content = (
    <div className='space-y-12px'>
      <Typography.Title level={5}>{t('associate project')}</Typography.Title>
      <div className='space-y-12px'>
        {audits}
        {projects}
        {(audits || [])?.length === 0 && (projects || [])?.length === 0 && (
          <Typography.Text type='secondary'>{t('project.nodata')}</Typography.Text>
        )}
      </div>
    </div>
  )

  return canRead ? content : <MosaicComponent />
}
